import {
  PaymentMethod,
  PaymentMethodType,
  PaymentLinkProvider,
  PaymentLinkConfigs,
  PaymentLinkConfig,
  EstablishmentCheckout,
  isPaymentMethodWithLink,
} from '@pedix-workspace/utils';

export const PAYMENT_LINK_PROVIDER_TO_TYPE: Record<PaymentLinkProvider, PaymentMethodType> = {
  mercadopago: PaymentMethodType.MERCADOPAGO_LINK,
  uala: PaymentMethodType.UALA_LINK,
  zenrise: PaymentMethodType.ZENRISE_LINK,
  dinelco: PaymentMethodType.DINELCO_LINK,
};

export const PAYMENT_LINK_TYPE_TO_PROVIDER: Record<string, PaymentLinkProvider> = {
  [PaymentMethodType.MERCADOPAGO_LINK]: 'mercadopago',
  [PaymentMethodType.UALA_LINK]: 'uala',
  [PaymentMethodType.ZENRISE_LINK]: 'zenrise',
  [PaymentMethodType.DINELCO_LINK]: 'dinelco',
};

const DEFAULT_PAYMENT_LINK_NAMES: Record<PaymentLinkProvider, string> = {
  mercadopago: 'Pago online (T. débito / crédito / Mercadopago)',
  uala: 'Pago online (T. débito / crédito / Ualá)',
  zenrise: 'Pago online (T. débito / crédito)',
  dinelco: 'Pago online (T. débito / crédito / Dinelco)',
};

const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = [
  { type: PaymentMethodType.CASH, name: 'Efectivo', active: true, removable: false, priceModifier: 'none' },
  { type: PaymentMethodType.CARD, name: 'Tarjeta débito/crédito', active: true, removable: false, priceModifier: 'none' },
  { type: PaymentMethodType.TRANSFER, name: 'Transferencia', active: false, removable: false, priceModifier: 'none' },
];

/**
 * Información mínima requerida de una integración para generar métodos de pago.
 * Genérico para soportar diferentes tipos de integraciones de pago (payment-link, etc.)
 * Compatible con Integration de @pedix-workspace/integrations-shared
 */
export type PaymentIntegrationInfo = {
  active: boolean;
  provider: string;
  types?: string[];
};

/**
 * @deprecated Usar PaymentIntegrationInfo en su lugar. Mantenido por compatibilidad hacia atrás.
 */
export type PaymentLinkIntegrationInfo = PaymentIntegrationInfo;

function createPaymentMethodFromIntegration(
  provider: PaymentLinkProvider,
  configs?: PaymentLinkConfigs
): PaymentMethod {
  const config: PaymentLinkConfig = configs?.[provider] || { priceModifier: 'none' };

  return {
    type: PAYMENT_LINK_PROVIDER_TO_TYPE[provider],
    name: config.name || DEFAULT_PAYMENT_LINK_NAMES[provider],
    active: true,
    removable: false,
    priceModifier: config.priceModifier || 'none',
    extraCharge: config.extraCharge,
    discount: config.discount,
    legend: config.legend,
    installmentOptions: config.installmentOptions,
  } as PaymentMethod;
}

export function getPaymentMethodValue(paymentMethod: Pick<PaymentMethod, 'type' | 'name'>): string {
  return paymentMethod.type || paymentMethod.name;
}

function sortByPaymentMethodsOrder(
  paymentMethods: PaymentMethod[],
  paymentMethodsOrder: string[] | undefined,
): PaymentMethod[] {
  if (!paymentMethodsOrder?.length) {
    return paymentMethods;
  }

  const getPosition = (paymentMethod: PaymentMethod) => {
    const position = paymentMethodsOrder.indexOf(getPaymentMethodValue(paymentMethod));
    return position === -1 ? paymentMethodsOrder.length : position;
  };

  return [...paymentMethods].sort((a, b) => getPosition(a) - getPosition(b));
}

export interface GetPaymentMethodsOptions {
  includeInactive?: boolean;
  /**
   * Tipos de integración a considerar para generar métodos de pago dinámicos.
   * Por defecto: ['payment-link'] para mantener compatibilidad.
   * Ejemplos: ['payment-link'], ['bank-transfer'], ['payment-link', 'bank-transfer']
   */
  integrationTypes?: string[];
}

export function getPaymentMethods(
  checkout:
    | Pick<EstablishmentCheckout, 'paymentMethods' | 'paymentLinkConfigs' | 'paymentMethodsOrder'>
    | null
    | undefined,
  integrations: Array<{ active: boolean; provider: string; types?: string[] }>,
  options?: GetPaymentMethodsOptions
): PaymentMethod[] {
  const includeInactive = options?.includeInactive ?? false;
  const integrationTypes = options?.integrationTypes ?? ['payment-link'];

  const basePaymentMethods =
    checkout?.paymentMethods?.length > 0
      ? checkout.paymentMethods
      : DEFAULT_PAYMENT_METHODS;

  const staticMethods = basePaymentMethods.filter(pm => !isPaymentMethodWithLink(pm));

  const dynamicMethods = integrations
    .filter(integration => {
      const isActive = integration.active;
      const hasMatchingType = integration.types?.some(type => integrationTypes.includes(type));
      return isActive && hasMatchingType;
    })
    .map(integration => {
      const provider = integration.provider as PaymentLinkProvider;
      return createPaymentMethodFromIntegration(provider, checkout?.paymentLinkConfigs);
    });

  const allMethods = sortByPaymentMethodsOrder(
    [...staticMethods, ...dynamicMethods],
    checkout?.paymentMethodsOrder,
  );

  const filteredMethods = includeInactive
    ? allMethods
    : allMethods.filter(pm => pm.active);

  return filteredMethods.sort((a, b) => {
    if (a.active === b.active) return 0;
    return a.active ? -1 : 1;
  });
}

export function extractPaymentLinkConfigs(paymentMethods: PaymentMethod[]): PaymentLinkConfigs {
  const configs: PaymentLinkConfigs = {};

  for (const pm of paymentMethods.filter(isPaymentMethodWithLink)) {
    const provider = PAYMENT_LINK_TYPE_TO_PROVIDER[pm.type];
    if (provider) {
      configs[provider] = {
        name: pm.name,
        priceModifier: pm.priceModifier || 'none',
        extraCharge: pm.extraCharge,
        discount: pm.discount,
        legend: pm.legend,
        installmentOptions: pm.installmentOptions,
      };
    }
  }

  return configs;
}
